import { Box, CircularProgress } from "@mui/material";
import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";

import PinEntryBoxes from "@Components/app/common/pin-entry-boxes";

import { Typography } from "@mui/material";

import ConfirmationDialog from "@Components/app/common/confirmation-dialog";
import { Actions, AppContext } from "@Context/common/general/context";
import {
  faCheckCircle,
  faEye,
  faEyeSlash,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import useI18n from "@i18n";
import { useTheme } from "@mui/material";
import KeypadVR from "../profile-switch/keypad-vr";

const PinSetupPanel = ({
  pinLength = 4,
  onSuccess = () => {},
  isFullscreen = false,
}: {
  pinLength?: number;
  onSuccess?: () => void;
  isFullscreen?: boolean;
}) => {
  const { i18n } = useI18n();
  const { state, dispatch } = useContext(AppContext);
  const theme: any = useTheme();
  //animate box
  const [ani, setAni] = useState<boolean>(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
  const [pinMissmatch, setPinMissmatch] = useState<boolean>(false);
  const defaultMessage: string = 'security.pin-setup.description';
  const [switchMessage, setSwitchMessage] = useState<string>(defaultMessage);
  const [pin, setPin] = useState<string>("");
  const [tempPin, setTempPin] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(false);

  //hide numbers/switches to dots
  const [hide, setHide] = useState<boolean>(true);

  //ref for text entry
  const fieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isFullscreen) state.app.events.exclusivePageEntered.emit();
    focusInput();
    return () => {
      if (isFullscreen) state.app.events.exclusivePageExited.emit();
    };
  }, []);

  useEffect(() => {
    // verification function (first check)
    if (pin.length === pinLength) storePin();
    if (pin.length === pinLength && tempPin.length === pinLength) checkPin();
    focusInput();
  }, [pin, tempPin]);

  const onPinChange = (e: ChangeEvent<HTMLInputElement>) => {
    //todo set the type
    //strip non-numbers from string
    let pinString = e.target.value.replace(/[^0-9]/g, "");
    if (pin.length < pinLength) setPin(pinString);
  };

  const checkPin = () => {
    let numCheck: number = parseInt(pin);
    let tempCheck: number = parseInt(tempPin);

    //todo : add function call
    setTimeout(() => {
      console.log("Delayed for 1 second.");
      {
        numCheck === tempCheck ? submitPin() : resetPin();
      } //start
      setPin("");
    }, 1000);
  };

  //todo: change this (used to simulate function call)
  const storePin = () => {
    setShowLoader(true);
    setTimeout(() => {
      setTempPin(pin);
      setPin("");
      setSwitchMessage(
        i18n('security.pin-setup.confirm-description')
      );
      setShowLoader(false);
    }, 1000);
  };

  const reset = () => {
    setShowLoader(false);
    setSwitchMessage("");
    setPinMissmatch(false);
    setPin("");
    setTempPin("");
  };

  const submitPin = () => {
    setIsSubmitting(true);
    dispatch({
      type: Actions.AuthActions.UpdatePin,
      payload: {
        pin: Number(pin),
      },
      onSuccess: onPinUpdated,
    });
    reset();
    setSwitchMessage("Submitting Pin");
  };

  const onPinUpdated = () => {
    setIsSubmitting(false);
    setShowSuccessDialog(true);
  };

  const onConfirm = () => {
    dispatch({
      type: Actions.CoreActions.UpdateState,
      payload: {
        user: {
          hasPin: true,
        },
      },
    });
    setShowSuccessDialog(false);
    onSuccess();
  };

  const resetPin = () => {
    setAni(true);
    setTimeout(() => setAni(false), 501);
    setSwitchMessage(defaultMessage);
    setPin("");
    setTempPin("");
    setPinMissmatch(true);
  };

  const focusInput = () => {
    if (fieldRef.current != null) {
      fieldRef.current.focus();
    }
  };

  const handleHide = () => {
    setHide(!hide);
    focusInput();
  };

  const handleKeypadVRPress = (num: string) => {
    const newPin = pin ? pin.concat(num) : num;
    if (pin.length < pinLength) setPin(newPin);
  };

  return !showSuccessDialog && !isSubmitting ? (
    <Box
      display={"flex"}
      alignItems="center"
      justifyContent="center"
      width={"fill-available"}
      sx={{ paddingLeft: theme.spacing(6), paddingRight: theme.spacing(6) }}
    >
      <>
        <Box
          style={{
            position: "relative",
            display: "flex-block",
            alignItems: "center",
            marginTop:
              state.device.isMobile || state.device.isVR ? "100px" : "52px",
          }}
        >
          <Typography
            variant="h4"
            style={{
              textAlign: "center",
              textTransform: "uppercase",
              marginBottom: theme.spacing(2),
            }}
            color="dsBase.v200"
          >
            {i18n(state.user.hasPin ? "security.change-pin.title" : "security.pin-setup.title")}
          </Typography>

          <Typography
            variant="body1"
            style={{
              textAlign: "center",
              color:
                pinMissmatch && !tempPin
                  ? theme.palette.dsDestructive.v400
                  : theme.palette.dsBase.v200,
              marginLeft: "auto",
              marginRight: "auto",
              marginBottom: theme.spacing(1),
              marginTop:
                state.device.isMobile || state.device.isVR
                  ? theme.spacing(0)
                  : theme.spacing(5),
              maxWidth:
                state.device.isMobile || state.device.isVR ? "400px" : "300px",
            }}
          >
            {i18n(pinMissmatch && !tempPin
              ? "security.pin-setup.mismatch-description"
              : switchMessage)}
          </Typography>
          <input
            ref={fieldRef}
            onKeyDown={(e: any) => {
              // Ignore + , . - and space keys (they break number input)
              if (
                e.keyCode === 187 ||
                e.keyCode === 188 ||
                e.keyCode === 189 ||
                e.keyCode === 190 ||
                e.keyCode === 32
              )
                e.preventDefault();
              e.stopPropagation();
            }}
            maxLength={pinLength}
            style={{
              height: "0px",
              opacity: 0,
              top: 0,
              left: 0,
              background: "pink",
            }}
            value={pin}
            autoFocus={false}
            type={"number"}
            onChange={onPinChange}
          />

          {showLoader ? (
            <>
              <br />
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            </>
          ) : (
            <>
              <PinEntryBoxes
                ani={ani}
                boxNum={pinLength}
                dots={hide}
                pin={pin}
              />
              <Box
                sx={{
                  position: "absolute",
                  display: "flex",
                  justifyContent: "center",
                  zIndex: 1,
                  width: "100%",
                }}
                onClick={() => {
                  handleHide();
                }}
              >
                <Icon
                  style={{
                    marginTop: "20px",
                  }}
                  color={theme.palette.dsPrimary.v200}
                  size="2x"
                  icon={hide ? faEye : faEyeSlash}
                />
              </Box>
            </>
          )}
          {state.device.isVR && (
            <Box sx={{ paddingTop: theme.spacing(6) }}>
              <KeypadVR
                disableKeyPad={showLoader}
                pin={pin}
                onKeyPressed={handleKeypadVRPress}
                onClearPin={() => {
                  setPin("");
                }}
              />
            </Box>
          )}
        </Box>
        {!state.device.isVR && (
          <Box
            onClick={() => {
              focusInput();
            }}
            style={{
              opacity: 0,
              position: "absolute",
              top: 0,
              left: 0,
              background: "green",
              height: "100vh",
              width: "100vw",
            }}
          >
            blockerBox
          </Box>
        )}
      </>
    </Box>
  ) : (
    <ConfirmationDialog
      open={showSuccessDialog}
      title={i18n("security.pin-setup.success-title")}
      message={
        i18n("security.pin-setup.success-description")
      }
      disableCancel={true}
      onConfirm={onConfirm}
      confirmLabel={i18n("ok")}
      buttonProps={[
        {
          size: "large",
        },
      ]}
      buttonStackProps={{
        justifyContent: "center",
      }}
      iconProps={{
        icon: faCheckCircle,
        size: "6x",
        color: theme.palette.success.light,
      }}
    />
  );
};

export default PinSetupPanel;
